import { AppInstance, AppMountTarget } from '@/app/instance'
import { AppRootComponent } from '@/app/root'
import { IAppApplicationSetup } from '@/app/setup'

export type AppMountHandler = (
  rootComponent: AppRootComponent,
  setup: IAppApplicationSetup,
  target?: AppMountTarget,
) => AppInstance

export interface IAppApplication {
  config: any
  use(plugin: unknown, ...options: unknown[]): IAppApplication
  component(name: string, definition: unknown): IAppApplication
  setGlobalProperty(name: string, value: unknown): IAppApplication
  setIntegration(name: 'router' | 'store', value: unknown): IAppApplication
  mount(target?: AppMountTarget): AppInstance
}

export function createApplication(
  rootComponent: AppRootComponent,
  mountHandler: AppMountHandler,
): IAppApplication {
  // We buffer all app registrations first and apply them during mount.
  // This keeps the same app API while the app bootstrap remains framework-agnostic.
  const setup: IAppApplicationSetup = {
    plugins: [],
    components: [],
    globalProperties: [],
    integrations: {},
  }

  const config = {
    productionTip: false,
  }

  return {
    config,
    use(plugin: unknown, ...options: unknown[]): IAppApplication {
      setup.plugins.push({ plugin, options })
      return this
    },
    component(name: string, definition: unknown): IAppApplication {
      setup.components.push({ name, definition })
      return this
    },
    setGlobalProperty(name: string, value: unknown): IAppApplication {
      setup.globalProperties.push({ name, value })
      return this
    },
    setIntegration(name: 'router' | 'store', value: unknown): IAppApplication {
      setup.integrations[name] = value
      return this
    },
    mount(target: AppMountTarget = '#app'): AppInstance {
      return mountHandler(rootComponent, setup, target)
    },
  }
}
